import { IApiTradingAccountCardCategory, IApiTradingPlatformType } from '@api/interfaces/data-contracts';
import { apiCopyTradingAccountCardUser } from '@api/services/copyTradingAccountCardUser.apiService';
import { apiTradingAccountCardUser } from '@api/services/tradingAccountCardUser.apiService';
import { apiTradingAccountUser } from '@api/services/tradingAccountUser.apiService';
import { accountCreatedSuccessfullyEvent } from '@components-member/TradingAccounts/AccountCreate/requests';
import { openAccountAnalyticsService } from '@components-member/TradingAccounts/analyticsEvents/openAccount.analytics.service';
import type { IAccountCreationQueryParams } from '@components-member/TradingAccounts/hooks/useAccountCreationQueryParams';
import { IApiTradingAccountCardInfoExt } from '@components-member/TradingAccounts/interfaces';
import { pushAccountCreateErrorEvent } from '@components-shared/googleAnalyticsEvents';
import { NotificationDialog } from '@components-shared/modal';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { getAccountCategoryType } from '@utils/getAccountCategoryType';
import { reactRouterService } from '@utils/library/reactRouter.service';
import { QUERY_PARAMS, TRADING_ACCOUNT_FORM_TYPE, TRADING_ACCOUNT_SOURCE } from 'global.enums';
import type { IUser } from 'global.interfaces';
import { i18n } from 'i18next';
import { makeAutoObservable, runInAction } from 'mobx';
import { takeLeading } from './mobx.helpers';
import type { MobxRootStore } from './mobx.store';

export class TradingAccountsStore {
  mobxStore: MobxRootStore;

  private _tradingAccountsCategories: IApiTradingAccountCardCategory[] = [];
  private _selfAccounts: IApiTradingAccountCardInfoExt[] = [];
  private _investorAccounts: IApiTradingAccountCardInfoExt[] = [];
  private _masterAccounts: IApiTradingAccountCardInfoExt[] = [];
  private _isTradingAccountBeingSynced = false;
  private _isFetchTradingAccountsProcessing = false;
  private _isCreateTradingAccountModalOpened = false;
  private _createSelfAccountCallbacks: Array<() => void> = [];

  constructor(mobxStore: MobxRootStore) {
    this.mobxStore = mobxStore;
    makeAutoObservable(this);
  }

  subscribeOnSelfAccountCreate = (callback: () => void) => {
    this._createSelfAccountCallbacks.push(callback);
  };

  unsubscribeOnSelfAccountCreate = (callback: () => void) => {
    this._createSelfAccountCallbacks = this._createSelfAccountCallbacks.filter((cb) => cb !== callback);
  };

  private _clearAllSelfAccountSubscriptions = () => {
    this._createSelfAccountCallbacks = [];
  };

  private _executeAllSelfAccountSubscriptions = () => {
    this._createSelfAccountCallbacks.forEach((callback) => callback());
  };

  fetchInitialAccountsData = async ({ isPreloaderEnable }: { isPreloaderEnable: boolean }) => {
    try {
      if (isPreloaderEnable) {
        runInAction(() => (this._isFetchTradingAccountsProcessing = true));
      }

      const [categoriesResponse, accountsResponse, investorAccountsResponse, masterAccountsResponse] =
        await Promise.all([
          apiTradingAccountCardUser.getCategories(),
          apiTradingAccountCardUser.getSelfTradingAccounts(),
          apiCopyTradingAccountCardUser.getInvestorAccounts(),
          apiCopyTradingAccountCardUser.getMasterAccounts(),
        ]);

      runInAction(() => {
        this._selfAccounts = accountsResponse.data;
        this._investorAccounts = investorAccountsResponse.data;
        this._masterAccounts = masterAccountsResponse.data;
        this._tradingAccountsCategories = categoriesResponse.data.sort((a, b) => a.priority - b.priority);
      });
    } catch (error) {
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    } finally {
      if (isPreloaderEnable) {
        runInAction(() => (this._isFetchTradingAccountsProcessing = false));
      }
    }
  };

  fetchSelfAccounts = takeLeading(async () => {
    try {
      const accountsResponse = await apiTradingAccountCardUser.getSelfTradingAccounts();

      runInAction(() => (this._selfAccounts = accountsResponse.data));
    } catch (error) {
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  });

  fetchSyncTradingAccount = async (tradingAccountId: number) => {
    try {
      this._isTradingAccountBeingSynced = true;
      await apiTradingAccountUser.sync(tradingAccountId);
      await this.fetchInitialAccountsData({ isPreloaderEnable: false });
    } catch (error) {
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    } finally {
      this._isTradingAccountBeingSynced = false;
    }
  };

  /**
   * @deprecated
   * Do NOT use outside AccountCreateEntry
   * Please use configureCreateTradingAccountModal instead
   */
  openCreateTradingAccountModal = () => {
    openAccountAnalyticsService.openAccountModalShowEvent(this.mobxStore.accountStore.userInfo);
    this._isCreateTradingAccountModalOpened = true;
  };

  configureCreateTradingAccountModal = (options: {
    queryParams: {
      [QUERY_PARAMS.OPEN_ACCOUNT]?: TRADING_ACCOUNT_FORM_TYPE;
      [QUERY_PARAMS.SOURCE]?: TRADING_ACCOUNT_SOURCE;
      [QUERY_PARAMS.PLATFORM_TYPE]?: IAccountCreationQueryParams['platformType'];
      [QUERY_PARAMS.CONTEST_ID]?: string | number;
    };
  }) => {
    reactRouterService.addUrlQueryParams(options.queryParams);
  };

  closeCreateTradingAccountModal = () => {
    openAccountAnalyticsService.openAccountModalCloseEvent(this.mobxStore.accountStore.userInfo);
    reactRouterService.deleteUrlQueryParams();
    this._isCreateTradingAccountModalOpened = false;
    this._clearAllSelfAccountSubscriptions();
  };

  createSelfTradeAccount = async (form: Record<string, unknown>, user: IUser) => {
    try {
      const response = await apiTradingAccountUser.createSelfTradeAccount(form);

      NotificationDialog.show(keyMap.tradingAccountsAccountSuccessfullyCreated);

      accountCreatedSuccessfullyEvent(response.data.idHash, form, user, response.data.accountType);
      this._executeAllSelfAccountSubscriptions();
      return response.data;
    } catch (error) {
      pushAccountCreateErrorEvent(error.response.data);
      handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
    }
  };

  getAccountTradeLink = (options: { i18n: i18n; accountId: number }) => {
    const { i18n } = options;

    const {
      accountStore: { userInfo, generalSettings },
      tradingAccountsStore,
    } = this.mobxStore;
    const { webTraderUrl } = generalSettings;

    const account = tradingAccountsStore.tradingAccountsAll.find((account) => account.id === options.accountId);

    const isTradingPlatformType = account?.type.tradingPlatformType === IApiTradingPlatformType.Gmi;

    if (!account) {
      return null;
    }

    const { isCommissionAccount, isMamCommissionAccount } = getAccountCategoryType({
      groupMappingType: account?.groupMappingType,
      masterId: account.master?.masterId || null,
      investorId: account.investor?.investorId || null,
    });

    if (
      !webTraderUrl ||
      !account ||
      !isTradingPlatformType ||
      !userInfo.isEdgeTradingEnabled ||
      isCommissionAccount ||
      isMamCommissionAccount
    ) {
      return null;
    }

    const {
      login,
      metaServerAddress,
      type: { displayName },
      id,
    } = account;

    const queryParams = new URLSearchParams({
      metaServerAddress,
      accountName: displayName,
      login: String(login),
      id: String(id),
      userId: String(userInfo.id),
      language: i18n.language,
    });

    return `${webTraderUrl}?${queryParams}`;
  };

  get tradingAccountsAll() {
    return [...this._selfAccounts, ...this._investorAccounts, ...this._masterAccounts];
  }

  get tradingAccountsSelf() {
    return this._selfAccounts;
  }

  get tradingAccountsCategories() {
    return this._tradingAccountsCategories;
  }

  get isFetchTradingAccountsProcessing() {
    return this._isFetchTradingAccountsProcessing;
  }

  get isTradingAccountBeingSynced() {
    return this._isTradingAccountBeingSynced;
  }

  get isCreateTradingAccountModalOpened() {
    return this._isCreateTradingAccountModalOpened;
  }
}
